/**
 * オフライン問題パックの IndexedDB スキーマ（idb ラッパー）。
 * 設計: .steering/20260719-capacitor-store-app/design.md §4.2
 */
import { openDB, DBSchema, IDBPDatabase } from "idb";
import { Question, ExamCategory } from "../types";

export interface OfflinePack {
  examCategory: string;
  questions: Question[];
  count: number;
  bytes: number;
  savedAt: string; // ISO 8601
}

export interface PendingAnswer {
  id?: number; // autoIncrement（新規作成時は未設定）
  questionId: string;
  selectedAnswer: number;
  isCorrect: boolean;
  answeredAt: string; // ISO 8601
}

interface KakumonOfflineDB extends DBSchema {
  packs: {
    key: string; // examCategory
    value: OfflinePack;
  };
  pendingAnswers: {
    key: number;
    value: PendingAnswer;
  };
}

const DB_NAME = "kakumon-offline";
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<KakumonOfflineDB>> | null = null;

export function getOfflineDB(): Promise<IDBPDatabase<KakumonOfflineDB>> {
  if (!dbPromise) {
    dbPromise = openDB<KakumonOfflineDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("packs")) {
          db.createObjectStore("packs", { keyPath: "examCategory" });
        }
        if (!db.objectStoreNames.contains("pendingAnswers")) {
          db.createObjectStore("pendingAnswers", {
            keyPath: "id",
            autoIncrement: true,
          });
        }
      },
    });
  }
  return dbPromise;
}

/**
 * テスト用: モジュール内でキャッシュしている DB 接続を閉じてリセットする。
 * close() せずに dbPromise だけ null にすると、次のテストの deleteDB() が
 * 既存の open 接続にブロックされて永久に解決しない（fake-indexeddb でも同様）
 */
export async function resetOfflineDBForTests(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
  }
  dbPromise = null;
}

export type { ExamCategory };
